/**
 * ReasoningPanel - Display reasoning behind a control conclusion
 *
 * Shows the logic chain in clear sections:
 * 1. Summary - "Warum diese Meldung?" with legal refs
 * 2. Calculation trigger - "Wie wurde das berechnet?" (opens side panel)
 * 3. Missing Items - "Fehlende Dokumente"
 * 4. Actions - "So läuft es weiter" with primary/secondary CTAs
 *
 * Part of the Event → Reasoning drill-down.
 */

import React from 'react';
import { Info, Calculator, Lightbulb, ChevronRight } from 'lucide-react';
import { LegalCitation } from '../../atoms/LegalCitation';
import { EventAction } from '../../atoms/EventAction';
import { MissingItemList } from '../../molecules/MissingItemList';
import type { MockReasoning, LegalReference, ControlEventAction } from '../../types';
import './ReasoningPanel.css';

export interface ReasoningPanelProps {
  /** The reasoning data to display */
  reasoning: MockReasoning;

  /** Map of legal reference IDs to full LegalReference objects */
  legalRefMap?: Record<string, LegalReference>;

  /** Actions available for this event */
  actions?: ControlEventAction[];

  /** Callback when a legal citation is clicked */
  onLegalClick?: (legalRefId: string) => void;

  /** Callback when an entity link is clicked */
  onEntityClick?: (entityId: string) => void;

  /** Callback when an action is clicked */
  onActionClick?: (actionId: string) => void;

  /** Callback when calculation details trigger is clicked */
  onCalculationClick?: () => void;

  /** Additional CSS class */
  className?: string;
}

export const ReasoningPanel: React.FC<ReasoningPanelProps> = ({
  reasoning,
  legalRefMap = {},
  actions = [],
  onLegalClick,
  onEntityClick,
  onActionClick,
  onCalculationClick,
  className = '',
}) => {
  const hasLegalBasis = reasoning.legalBasis && reasoning.legalBasis.length > 0;
  const hasCalculationDetails = reasoning.calculationDetails || reasoning.factors?.length || reasoning.calculation;
  const hasMissingItems = reasoning.missingItems && reasoning.missingItems.length > 0;
  const hasActions = actions.length > 0;
  const hasActionRecommendation = !!reasoning.actionRecommendation;
  const showWhatToDo = hasActions || hasActionRecommendation;

  const primaryAction = actions.find((a) => a.type === 'primary');
  const secondaryActions = actions.filter((a) => a.type !== 'primary');

  return (
    <div className={`reasoning-panel ${className}`}>
      {/* Summary with integrated legal basis */}
      <section className="reasoning-panel__section reasoning-panel__summary">
        <div className="reasoning-panel__section-header">
          <Info className="reasoning-panel__section-icon" aria-hidden="true" />
          <h4 className="reasoning-panel__section-title">Warum diese Meldung?</h4>
        </div>
        <p className="reasoning-panel__summary-text">
          {reasoning.summary}
          {hasLegalBasis && (
            <span className="reasoning-panel__legal-refs-inline">
              {reasoning.legalBasis!.map((ref) => (
                <LegalCitation
                  key={ref.id}
                  citation={ref.shortCitation}
                  onClick={() => onLegalClick?.(ref.id)}
                />
              ))}
            </span>
          )}
        </p>
      </section>

      {/* Calculation Details Trigger - replaces inline factors */}
      {hasCalculationDetails && onCalculationClick && (
        <button
          type="button"
          className="reasoning-panel__calc-trigger"
          onClick={onCalculationClick}
        >
          <div className="reasoning-panel__calc-trigger-content">
            <Calculator className="reasoning-panel__calc-trigger-icon" aria-hidden="true" />
            <span className="reasoning-panel__calc-trigger-text">Wie wurde das berechnet?</span>
          </div>
          <ChevronRight className="reasoning-panel__calc-trigger-chevron" />
        </button>
      )}

      {/* Missing Items */}
      {hasMissingItems && (
        <section className="reasoning-panel__section reasoning-panel__missing">
          <MissingItemList
            title="Fehlende Dokumente"
            items={reasoning.missingItems!.map((item) => ({
              label: item.label,
              reason: item.reason,
              linkedEntityId: item.linkedEntityId,
              // Use provided name, don't try to look up entity IDs in legal map
              linkedEntityName: item.linkedEntityName,
              legalRefId: item.legalRefId,
              legalCitation: item.legalRefId ? legalRefMap[item.legalRefId]?.shortCitation : item.legalCitation,
              priority: item.priority || 'warning',
            }))}
            onEntityClick={onEntityClick}
          />
        </section>
      )}

      {/* Actions - "So läuft es weiter" */}
      {showWhatToDo && (
        <section className="reasoning-panel__section reasoning-panel__actions">
          <div className="reasoning-panel__section-header">
            <Lightbulb className="reasoning-panel__section-icon" aria-hidden="true" />
            <h4 className="reasoning-panel__section-title">So läuft es weiter</h4>
          </div>
          {hasActionRecommendation && (
            <p className="reasoning-panel__action-recommendation">
              {reasoning.actionRecommendation}
            </p>
          )}
          {hasActions && (
            <div className="reasoning-panel__actions-list">
              {primaryAction && (
                <EventAction
                  label={primaryAction.label}
                  type="primary"
                  icon={primaryAction.icon as any}
                  onClick={() => onActionClick?.(primaryAction.id)}
                />
              )}
              {secondaryActions.map((action) => (
                <EventAction
                  key={action.id}
                  label={action.label}
                  type={action.type === 'link' ? 'link' : 'secondary'}
                  icon={action.icon as any}
                  onClick={() => onActionClick?.(action.id)}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default ReasoningPanel;
